import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Stack } from '@yandex-int/k-common/typings';
import produce from 'immer';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import { DEFAULT_PAGE_TO_LOAD } from 'platform-components/constants';

import { LibraryAction, LibraryStoreState } from '../library.common/typings';

import * as constants from './constants';

const initialState: LibraryStoreState = {
    attributes: {
        /* [ThemeID]: [{attr1}, {attr2}, {attr3}, ...]*/
    },
    availableAttributes: {
        /* [ThemeID]: [{attr1}, {attr2}, {attr3}, ...]*/
    },
    filterValues: {
        /* [FilterSLug]: FilterValue*/
    },
    stackProblemsLoadingStatus: LOADING_STATUSES.UNSENT,
    selectedStackId: null,
    deepLinkStack: null,
    themesDetails: {},
    themeDetailsLoadingStatus: {},
    stacks: {},
    stacksLoadedPages: {},
    stacksLoadingStatus: LOADING_STATUSES.UNSENT,
    canLoadMoreStacks: {},
    coursesPreview: [],
    coursesPreviewLoading: LOADING_STATUSES.UNSENT,
};

const libraryReducer = (state = initialState, action: LibraryAction) => {
    // eslint-disable-next-line complexity
    return produce(state, (draft) => {
        switch (action.type) {
            case constants.GET_STACK_PROBLEMS_REQUEST: {
                draft.stackProblemsLoadingStatus = LOADING_STATUSES.LOADING;
                break;
            }
            case constants.GET_STACK_PROBLEMS_SUCCESS: {
                const { problems, themeId } = action;
                const { stacks } = state;

                const stateStacks = get(stacks, [themeId], [] as Array<Stack>);

                const mergedValues = stateStacks.reduce((acc, existingStack) => {
                    const stackFromRequest = problems.find(({ id }) => id === existingStack.id);

                    if (stackFromRequest) {
                        acc.push(stackFromRequest);
                    } else {
                        acc.push(existingStack);
                    }

                    return acc;
                }, [] as Array<Stack>);

                draft.stacks = {
                    ...stacks,
                    [themeId]: mergedValues,
                };
                draft.stackProblemsLoadingStatus = LOADING_STATUSES.SUCCESS;
                break;
            }
            case constants.GET_STACK_PROBLEMS_ERROR: {
                draft.stackProblemsLoadingStatus = LOADING_STATUSES.ERROR;
                break;
            }
            case constants.GET_ALL_ATTRIBUTES_SUCCESS: {
                const { themeId, attributes } = action;

                draft.attributes[themeId] = attributes;

                break;
            }

            case constants.GET_AVAILABLE_ATTRIBUTES_SUCCESS: {
                const { themeId, attributes } = action;

                draft.availableAttributes[themeId] = attributes;

                break;
            }

            case constants.SET_FILTER_VALUE: {
                const { filterSlug, filterValue } = action;

                if (isNil(filterValue)) {
                    delete draft.filterValues[filterSlug];
                } else {
                    draft.filterValues[filterSlug] = filterValue;
                }

                draft.stacks = {};

                break;
            }

            case constants.SET_FILTERS: {
                draft.filterValues = action.filters;
                break;
            }

            case constants.SELECT_STACK:
            case constants.SELECT_DEEP_LINK_STACK:
                draft.selectedStackId = action.stackId;
                break;

            case constants.SET_DEEP_LINK_STACK:
                draft.deepLinkStack = action.stack;
                break;

            case constants.GET_THEME_DETAILS_REQUEST: {
                const { themeId } = action;

                draft.themeDetailsLoadingStatus[themeId] = LOADING_STATUSES.LOADING;
                break;
            }

            case constants.GET_THEME_DETAILS_SUCCESS: {
                const { themeId, themeDetails } = action;

                draft.themesDetails[themeId] = themeDetails;
                draft.themeDetailsLoadingStatus[themeId] = LOADING_STATUSES.SUCCESS;

                break;
            }

            case constants.GET_THEME_DETAILS_ERROR: {
                const { themeId } = action;

                draft.themeDetailsLoadingStatus[themeId] = LOADING_STATUSES.ERROR;
                break;
            }

            case constants.LOAD_STACKS_REQUEST:
                draft.stacksLoadingStatus = LOADING_STATUSES.LOADING;
                break;

            case constants.LOAD_STACKS_SUCCESS: {
                const { stacks, themeId, page, canLoadMore } = action;

                const stateStacks = get(state, ['stacks', themeId], [] as Array<Stack>);

                if (page === DEFAULT_PAGE_TO_LOAD) {
                    draft.stacks[themeId] = stacks;
                } else {
                    draft.stacks[themeId] = uniqBy(stateStacks.concat(stacks), 'id');
                }

                draft.stacksLoadedPages[themeId] = page;
                draft.stacksLoadingStatus = LOADING_STATUSES.SUCCESS;
                draft.canLoadMoreStacks[themeId] = canLoadMore;
                break;
            }

            case constants.LOAD_STACKS_ERROR:
                draft.stacksLoadingStatus = LOADING_STATUSES.ERROR;
                break;

            case constants.CLEAR_STACKS:
                draft.stacks = {};
                break;

            case constants.GET_COURSES_PREVIEW_REQUEST:
                draft.coursesPreviewLoading = LOADING_STATUSES.LOADING;
                break;

            case constants.GET_COURSES_PREVIEW_SUCCESS:
                draft.coursesPreview = action.payload;
                draft.coursesPreviewLoading = LOADING_STATUSES.SUCCESS;
                break;

            case constants.GET_COURSES_PREVIEW_ERROR:
                draft.coursesPreviewLoading = LOADING_STATUSES.ERROR;
                break;

            default:
                break;
        }
    });
};

export default libraryReducer;
